import { inject, Injectable } from '@angular/core';
import { IndicatorStore } from '../stores/indicator/indicator.state';
import { IndicatorDataStore } from '../stores/indicatpr-data/indicator-data.state';
import { CategoryStore } from '../stores/category/category.state';
import { StoreRegistryService } from './store-registry.service';
import { Indicator } from '../models/indicator';
import { IndicatorData } from '../models/indicator-data';
import { Category } from '../models/category';
import { BulkActionStore } from '../stores/bulk.state';
import { Tag } from '../models/tag';
import { EnhancedMessage } from '../models/base-entity';
import { TopicStore } from '../stores/topic/topic.state';
import { BookmarkCategoryStore } from '../stores/bookmark-category/bookmark-category.state';
import { WorkspaceChildren, WorkspaceTypeKey } from '../models/workspace';
import { makeUniqueNumberArray } from '../utility/array.utility';
import { DataLayerTypeStore } from '../stores/data-layer-type/data-layer-type.state';

type BulkTypes = Indicator | Category | IndicatorData;
type BulkEntityTypes = 'Indicator' | 'Category' | 'IndicatorData';

@Injectable({
  providedIn: 'root',
})
export class BulkOperationService {
  readonly bulkStore = inject(BulkActionStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly indicatorDataStore = inject(IndicatorDataStore);
  readonly categoryStore = inject(CategoryStore);
  readonly topicStore = inject(TopicStore);
  readonly dataLayerTypeStore = inject(DataLayerTypeStore);
  readonly bookmarkCategoryStore = inject(BookmarkCategoryStore);

  constructor(private storeGetter: StoreRegistryService) {}

  /**
   * Executes a specific bulk action on the provided entity type.
   *
   * @param {BulkEntityTypes} entityType - The type of entity on which the action will be performed.
   * @param {string} action - The action to be performed. Supported actions include 'remove', 'lock', 'unlock', 'publish', 'move', 'addToSet', and 'addTags'.
   * @return {void} No return value.
   */
  confirm(entityType: BulkEntityTypes, action: string): void {
    switch (action) {
      case 'remove':
        this.bulkDelete(entityType);
        break;
      case 'lock':
        this.bulkLock(entityType);
        break;
      case 'unlock':
        this.bulkUnlock(entityType);
        break;
      case 'publish':
        this.bulkPublish();
        break;
      case 'unpublish':
        this.bulkUnpublish();
        break;
      case 'move':
        this.bulkMove(entityType);
        break;
      case 'addToSet':
        this.bulkMoveIndicatorToSet();
        break;
      case 'addTags':
        this.bulkAddTagsToIndicator();
        break;
    }
  }

  /**
   * Deletes multiple items of the specified entity type by marking them as deleted.
   *
   * @param {BulkEntityTypes} entityType - The type of entity for which items are to be deleted.
   * @return {void} This method does not return any value.
   */
  bulkDelete(entityType: BulkEntityTypes): void {
    const store = this.storeGetter.getStore(entityType);

    let items: BulkTypes[] = store.selection();
    items = items.map(item => {
      item.deleted = true;
      return item;
    });
    store.removeMultiple({
      items,
      message: this.getBulkMessageByActionAndEntityType('remove', entityType),
    });
  }

  /**
   * Locks multiple entities of the specified type by setting their locked property to true.
   *
   * @param {BulkEntityTypes} entityType - The type of entities to be locked.
   * @return {void} This method does not return a value.
   */
  bulkLock(entityType: BulkEntityTypes): void {
    const store = this.storeGetter.getStore(entityType);

    let items: BulkTypes[] = store.selection();
    items = items.map(item => {
      item.locked = true;
      return item;
    });
    store.updateMultiple({
      items,
      message: this.getBulkMessageByActionAndEntityType('lock', entityType),
    });
  }

  /**
   * Unpublish multiple entities of the specified type by setting their locked property to true.
   *
   * @return {void} This method does not return a value.
   */
  bulkUnpublish(): void {
    const store = this.storeGetter.getStore('IndicatorData');

    let items: BulkTypes[] = store.selection();
    items = items.map(item => {
      item.published = false;
      return item;
    });
    store.updateMultiple({
      items,
      message: this.getBulkMessageByActionAndEntityType(
        'unpublish',
        'IndicatorData'
      ),
    });
  }

  /**
   * Unlocks a bulk selection of entities for a specific entity type by setting their locked status to false.
   *
   * @param {BulkEntityTypes} entityType - The type of entities to unlock.
   * @return {void} This method does not return a value.
   */
  bulkUnlock(entityType: BulkEntityTypes): void {
    const store = this.storeGetter.getStore(entityType);

    let items: BulkTypes[] = store.selection();
    items = items.map(item => {
      item.locked = false;
      return item;
    });
    store.updateMultiple({
      items,
      message: this.getBulkMessageByActionAndEntityType('unlock', entityType),
    });
  }

  /**
   * Publishes multiple selected indicator data items by setting their `published` property to true
   * and updates the data store accordingly.
   *
   * @return {void} This method does not return a value.
   */
  bulkPublish(): void {
    const store = this.storeGetter.getStore('IndicatorData');

    let items: IndicatorData[] = store.selection();
    items = items.map(item => {
      item.published = true;
      return item;
    });
    store.updateMultiple({
      items,
      message: this.getBulkMessageByActionAndEntityType(
        'publish',
        'IndicatorData'
      ),
    });
  }

  /**
   * Moves multiple entities of a specified type to a new location or category.
   *
   * @param {BulkEntityTypes} entityType - The type of entity to be moved.
   *                                       Possible values include 'Indicator' and other entity types.
   * @return {void} This method does not return a value.
   */
  bulkMove(entityType: BulkEntityTypes): void {
    if (entityType === 'Indicator') {
      this.bulkMoveIndicator();
    } else {
      this.bulkMoveCategory();
    }
  }

  /**
   * Moves multiple indicators to a new category in bulk.
   * The method retrieves the selected indicators and updates their category
   * to the specified category from `bulkStore`. If no category is provided, the method terminates.
   *
   * @return {void} No value is returned by this method.
   */
  bulkMoveIndicator(): void {
    const newCategory = this.bulkStore.category()?._id ?? null;
    if (!newCategory) return;
    let items: Indicator[] = this.indicatorStore.selection();
    items = items.map(item => {
      item.category = newCategory;
      return item;
    });
    this.indicatorStore.updateMultiple({
      items,
      message: this.getBulkMessageByActionAndEntityType('move', 'Indicator'),
    });
  }

  /**
   * Moves multiple categories to a new parent category in bulk.
   * Retrieves the new category identifier from bulkStore,
   * filters the selected categories from categoryStore,
   * and updates their parent property to the new category identifier.
   *
   * @return {void} Does not return a value.
   */
  bulkMoveCategory(): void {
    const newCategory = this.bulkStore.category()?._id ?? null;
    if (!newCategory) return;
    let items: Category[] = this.categoryStore.selection();
    items = items.map(item => {
      item.parent = newCategory;
      return item;
    });
    this.categoryStore.updateMultiple({
      items,
      message: this.getBulkMessageByActionAndEntityType('move', 'Category'),
    });
  }

  /**
   * Moves selected indicators to a new set if they are not already part of a set.
   * This method fetches the target set from the `bulkStore` and updates the parent
   * property of eligible indicators. Indicators already associated with a set are filtered out.
   *
   * @return {void} This method does not return a value.
   */
  bulkMoveIndicatorToSet(): void {
    const newSet = this.bulkStore.set()?._id ?? null;
    if (!newSet) return;
    let items: Indicator[] = this.indicatorStore.selection();
    items = items
      .filter(item => {
        return !item.isSet;
      })
      .map(item => {
        item.parent = newSet;
        return item;
      });
    this.indicatorStore.updateMultiple({
      items,
      message: this.getBulkMessageByActionAndEntityType(
        'addToSet',
        'Indicator'
      ),
    });
  }

  /**
   * Adds multiple tags to a collection of indicators in bulk.
   *
   * This method retrieves the selected indicators and applies the tags from the bulk store
   * to each indicator. It then updates the indicators in bulk within the indicator store
   * and displays a message confirming the action.
   *
   * @return {void} This method does not return a value.
   */
  bulkAddTagsToIndicator(): void {
    let items: Indicator[] = this.indicatorStore.selection();
    items = items.map(item => {
      const tags = this.bulkStore.tags();
      item.tags = tags.map((tag: Tag) => {
        return tag._id;
      });
      return item;
    });
    this.indicatorStore.updateMultiple({
      items,
      message: this.getBulkMessageByActionAndEntityType('addTags', 'Indicator'),
    });
  }

  /**
   * Retrieves a bulk message configuration based on the specified action and entity type.
   *
   * @param {string} action - The action performed, which determines the message content.
   * @param {BulkEntityTypes} entityType - The type of entity that the action is performed on.
   * @return {EnhancedMessage} An object containing details of the message including severity, summary, detail, and life duration.
   */
  getBulkMessageByActionAndEntityType(
    action: string,
    entityType: BulkEntityTypes
  ): EnhancedMessage {
    return {
      severity: 'success',
      summary: 'Sammelaktion erfolgreich durchgeführt!',
      detail: `${this.getEntityNameByEntityType(entityType)} wurden erfolgreich ${this.getActionNameByAction(action)}.`,
      life: 5000,
    };
  }

  /**
   * Retrieves the name associated with a given entity type.
   *
   * @param {BulkEntityTypes} entityType - The type of the entity for which the name is to be retrieved.
   *                                        Possible values include 'Indicator', 'Category', 'IndicatorData'.
   * @return {string} The corresponding name for the provided entity type.
   */
  getEntityNameByEntityType(entityType: BulkEntityTypes): string {
    switch (entityType) {
      case 'Indicator':
        return 'Indikatoren';
      case 'Category':
        return 'Indikatorenordner';
      case 'IndicatorData':
        return 'Daten';
    }
  }

  /**
   * Returns the corresponding action name in German based on the provided action string.
   *
   * @param {string} action - The action key for which the corresponding German action name is returned. Possible values are: 'remove', 'lock', 'unlock', 'addTags', 'addToSet', 'move', 'publish'.
   * @return {string} The German action name corresponding to the provided action key. If the action does not match any case, it returns 'aktualisiert' as the default value.
   */
  getActionNameByAction(action: string): string {
    switch (action) {
      case 'remove':
        return 'entfernt';
      case 'lock':
        return 'gesperrt';
      case 'unlock':
        return 'entsprerrt';
      case 'addTags':
        return 'verschlagwortet';
      case 'addToSet':
        return 'versettet';
      case 'move':
        return 'verschoben';
      case 'publish':
        return 'veröffentlicht';
      case 'unpublish':
        return 'intern geschaltet';
    }
    return 'aktualisiert';
  }

  /**
   * Adds a workspace to a set of entities by organizing and processing the provided workspace children.
   *
   * @param {string} workspaceId - The unique identifier of the workspace to be added.
   * @param {WorkspaceChildren} workspaceChildren - An object where keys represent types of workspace entities and values are arrays of entity IDs.
   * @return {void} - Does not return a value.
   */
  addWorkspaceToEntities(
    workspaceId: string,
    workspaceChildren: WorkspaceChildren
  ) {
    // Group dataLayerType and poiDataLayerType together since they share the same store
    const entries = Object.entries(workspaceChildren) as [
      WorkspaceTypeKey,
      string[],
    ][];
    const dataLayerTypeIds: string[] = [];
    const poiDataLayerTypeIds: string[] = [];
    const otherEntries: [WorkspaceTypeKey, string[]][] = [];

    entries.forEach(([type, ids]) => {
      if (type === 'dataLayerType') {
        dataLayerTypeIds.push(...ids);
      } else if (type === 'poiDataLayerType') {
        poiDataLayerTypeIds.push(...ids);
      } else {
        otherEntries.push([type, ids]);
      }
    });

    // Process combined dataLayerType and poiDataLayerType
    if (dataLayerTypeIds.length > 0 || poiDataLayerTypeIds.length > 0) {
      this.updateWorkspaceForDataLayerTypes(
        workspaceId,
        dataLayerTypeIds,
        poiDataLayerTypeIds
      );
    }

    // Process other types normally
    otherEntries.forEach(([type, ids]) =>
      this.updateWorkspaceForEntities(workspaceId, ids, type)
    );
  }

  /**
   * Updates workspace assignments for DataLayerType entities (both Point and Polygon types).
   * Since dataLayerType and poiDataLayerType share the same store, this method handles both
   * in a single update call to prevent race conditions.
   *
   * @param {string} workspaceId - The ID of the workspace to manage.
   * @param {string[]} dataLayerTypeIds - Array of Polygon DataLayerType IDs that should have the workspace.
   * @param {string[]} poiDataLayerTypeIds - Array of Point DataLayerType IDs that should have the workspace.
   * @return {void} This method does not return a value.
   */
  private updateWorkspaceForDataLayerTypes(
    workspaceId: string,
    dataLayerTypeIds: string[],
    poiDataLayerTypeIds: string[]
  ): void {
    const store = this.dataLayerTypeStore;
    const polygonIdsSet = new Set(
      dataLayerTypeIds.map(id => this.parseEntityId(id).entityId)
    );
    const pointIdsSet = new Set(
      poiDataLayerTypeIds.map(id => this.parseEntityId(id).entityId)
    );

    const items = store.asArray().filter((entity: any) => {
      const isPolygon = entity.type === 'Polygon';
      const isPoint = entity.type === 'Point';
      const shouldHaveWorkspace =
        (isPolygon && polygonIdsSet.has(entity._id)) ||
        (isPoint && pointIdsSet.has(entity._id));

      const relevantIds = isPolygon ? dataLayerTypeIds : poiDataLayerTypeIds;
      const matchingId = relevantIds.find(
        id => this.parseEntityId(id).entityId === entity._id
      );
      const key = matchingId ? this.parseEntityId(matchingId).key : undefined;

      return this.updateEntityWorkspace(
        entity,
        workspaceId,
        shouldHaveWorkspace,
        key
      );
    });

    if (items.length) {
      store.updateMultiple({ items, message: { avoid: true } });
    }
  }

  /**
   * Updates workspace assignments for entities of a given type.
   * Adds the workspace to specified entities and removes it from all others.
   * This ensures a complete replacement operation in a single update call per store.
   *
   * @param {string} workspaceId - The ID of the workspace to manage.
   * @param {string[]} idsToAdd - Array of entity IDs that should have the workspace.
   * @param {WorkspaceTypeKey} type - The type of entity store to target.
   * @return {void} This method does not return a value.
   */
  private updateWorkspaceForEntities(
    workspaceId: string,
    idsToAdd: string[],
    type: WorkspaceTypeKey
  ): void {
    const store = this.getStoreByType(type);
    const idsToAddSet = new Set(
      idsToAdd.map(id => this.parseEntityId(id).entityId)
    );

    const items = store.asArray().filter((entity: any) => {
      const shouldHaveWorkspace = idsToAddSet.has(entity._id);
      const matchingId = idsToAdd.find(
        id => this.parseEntityId(id).entityId === entity._id
      );
      const key = matchingId ? this.parseEntityId(matchingId).key : undefined;

      return this.updateEntityWorkspace(
        entity,
        workspaceId,
        shouldHaveWorkspace,
        key
      );
    });

    if (items.length) {
      store.updateMultiple({ items, message: { avoid: true } });
    }
  }

  /**
   * Parses an entity ID string that may contain a key separated by '#'.
   *
   * @param {string} id - The ID string to parse (format: "entityId" or "entityId#key").
   * @return {{ entityId: string; key?: string }} Object containing the entity ID and optional key.
   */
  private parseEntityId(id: string): { entityId: string; key?: string } {
    const [entityId, key] = id.split('#');
    return { entityId, key };
  }

  /**
   * Gets the appropriate store based on the workspace type key.
   *
   * @param {WorkspaceTypeKey} type - The type of workspace entity.
   * @return {any} The corresponding store instance.
   */
  private getStoreByType(type: WorkspaceTypeKey): any {
    switch (type) {
      case 'indicatorCategory':
        return this.categoryStore;
      case 'bookmarkCategory':
        return this.bookmarkCategoryStore;
      case 'topic':
        return this.topicStore;
      case 'poiDataLayerType':
      case 'dataLayerType':
        return this.dataLayerTypeStore;
    }
  }

  /**
   * Updates an entity's workspace array by adding or removing a workspace ID.
   *
   * @param {any} entity - The entity to update.
   * @param {string} workspaceId - The workspace ID to add or remove.
   * @param {boolean} shouldAdd - Whether to add (true) or remove (false) the workspace.
   * @param {string} key - Optional key to append to the workspace ID.
   * @return {boolean} True if the entity's workspaces array was modified, false otherwise.
   */
  private updateEntityWorkspace(
    entity: any,
    workspaceId: string,
    shouldAdd: boolean,
    key?: string
  ): boolean {
    if (!entity.workspaces) {
      entity.workspaces = [];
    }

    const workspaceWithKey = key ? `${workspaceId}#${key}` : workspaceId;

    if (shouldAdd) {
      // Check if workspace already exists
      const alreadyExists = entity.workspaces.some(
        (ws: string) => ws === workspaceWithKey
      );

      if (!alreadyExists) {
        entity.workspaces.push(workspaceWithKey);
        entity.workspaces = makeUniqueNumberArray(entity.workspaces);
        return true;
      }
      return false;
    } else {
      // Remove workspace - check length before filtering
      const originalLength = entity.workspaces.length;
      entity.workspaces = entity.workspaces.filter(
        (ws: string) => this.parseEntityId(ws).entityId !== workspaceId
      );

      // Return true if anything was removed
      return entity.workspaces.length !== originalLength;
    }
  }
}
